<script>
export default {
  title: 'Project 1: Pig Latin',
  courseId: 'c'
}
</script>

<template>
  <section>

    <article>
      <h2>Readings</h2>
      <ul>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/idot-basics/pcp3-chp-1"><cite>Practical C Programming (3rd Edition)</cite> Chapter 1</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/2dot-basics-of-program-writing/pcp3-chp-2-sect-1"><cite>Practical C Programming (3rd Edition)</cite> Sections 2.1 - 2.3</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/idot-basics/pcp3-chp-23-sect-6"><cite>Practical C Programming (3rd Edition)</cite> Chapter 3</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/idot-basics/pcp3-chp-4"><cite>Practical C Programming (3rd Edition)</cite> Chapter 4</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/idot-basics/pcp3-chp-6"><cite>Practical C Programming (3rd Edition)</cite> Chapter 6</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/iidot-simple-programming/pcp3-chp-8"><cite>Practical C Programming (3rd Edition)</cite> Chapter 8</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/9dot-variable-scope-and-functions/pcp3-chp-9-sect-1"><cite>Practical C Programming (3rd Edition)</cite> Sections 9.1 - 9.4</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/ivdot-other-language-features/pcp3-app-c"><cite>Practical C Programming (3rd Edition)</cite> Appendix C</a></li>
      </ul>
    </article>

    <article>
      <h2>Background</h2>

      <p>Pig Latin is a language parents speak to each other when they don't want
        their children to understand them. Each English word has a Pig Latin
        translation; you produce it as follows.</p>

      <ol>
        <li>If the English word begins with a vowel ("a", "e", "i", "o", "u"), add "yay" to the end of the word to produce the Pig Latin translation.</li>
        <li>
          <p>If the English word begins with a consonant (a non-vowel), move all
            leading consonants to the end of the word and add "ay" to produce the
            Pig Latin translation. You should treat the letter `y' as a vowel when
            it occurs after the first character in a word. You should also treat
            the letter pair "qu" together as a consonant. Here are some
            examples.</p>

          <table cellpadding="5" cellspacing="0" style="margin: 0 auto">
            <tbody><tr>
              <th>English</th>
              <th>Pig Latin</th>
            </tr>
            <tr>
              <td>yes</td>
              <td>esyay</td>
            </tr>
            <tr>
              <td>example</td>
              <td>exampleyay</td>
            </tr>
            <tr>
              <td>bygone</td>
              <td>ygonebay</td>
            </tr>
            <tr>
              <td>test</td>
              <td>esttay</td>
            </tr>
            <tr>
              <td>quiz</td>
              <td>izquay</td>
            </tr>
            <tr>
              <td>strip</td>
              <td>ipstray</td>
            </tr>
            <tr>
              <td>trips</td>
              <td>ipstray</td>
            </tr>
            <tr>
              <td>schnitzel</td>
              <td>itzelschnay</td>
            </tr>
            <tr>
              <td>rhyme</td>
              <td>ymerhay</td>
            </tr>
            </tbody></table>
        </li>
      </ol>
    </article>

    <article>
      <h2>Problem</h2>
      <p>Write a program in C to read a word from standard input and print its
        Pig Latin translation on a line by itself on standard output. You may
        assume that an English word starts with no more than four consonants.</p>
    </article>

    <article>
      <h2>Miscellaneous Requirements</h2>

      <p>C code is rather dense. Thus your functions should be small, certainly
        at most a screenful of code.</p>

      <p>Your program need not use arrays. You may assume that an English word
        starts with no more than four consonants. (This assumption makes it
        possible to avoid arrays.) You may also assume that each "q" is followed by
        a "u". Test your program at least on the examples given above.</p>
    </article>

    <article>
      <h2>System Information</h2>
      <p>The line <code>#include &lt;stdio.h&gt;</code> at the start of your
        program will allow you to use the predefined constant <code>EOF</code> and
        the <code>getchar</code> and <code>putchar</code> routines for input and
        output.</p>
    </article>

    <article>
      <h2>Helpful Hints</h2>

      <p>C gives you plenty of rope to hang yourself. To save your neck, you
        should spend time decomposing the problem into manageable pieces (i.e.
        separate functions!) before trying to code anything, and then test each
        piece separately. You may test your functions individually by writing a
        tiny "driver" <code>main()</code> that simply gets test data and calls the
        function.</p>

      <p>Some aspects of C are sure to trip up the novice C programmer, for
        instance, the placement of semicolons and parentheses, the need to "break"
        out of a case in a <code>switch</code> statement, and the indexing of
        arrays. The compiler and run-time system are sometimes of little help in
        finding these errors. Advice: Start with something simple, get it working,
        and add small pieces to it one at a time. Tutors will ask you, when you
        bring them bugs, "What was the last thing you did that worked?" You should
        have a good answer.</p>

      <p>Arrays are not necessary for this problem. (Why? If you need a hint,
        consider a program that simply echoes characters from the standard input to
        standard output. How many variables does that program need?)</p>
    </article>

    <article>
      <h2>Checklist</h2>
      <p>A satisfactory grade on this assignment requires the following:</p>
      <ul>
        <li>Correctly working program, tested as suggested</li>
        <li>Short functions</li>
        <li>Good style and layout</li>
        <li>Reasonable names for variables, functions, etc.</li>
        <li>Loops and blocks indented according to the given guidelines</li>
        <li>Informative comments accompanying every function</li>
      </ul>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/c/PigLatin.vue